import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, Mail, MapPin, Building, User, AlertCircle, Shield, CheckCircle2, XCircle, Briefcase, SlidersHorizontal, ChevronDown, ChevronUp, Eye, X, Users } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { Pagination } from '../components/ui/Pagination';
import { EmployeeFormModal } from '../components/EmployeeFormModal';
import { getEmployees, addEmployee, updateEmployee, deleteEmployee } from '../utils/companyManagement';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured, generateSecureId } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
// Helper function to get avatar color based on first letter
const getAvatarColor = (name: string) => {
  const letter = name.charAt(0).toUpperCase();
  if (letter >= 'A' && letter <= 'F') return 'bg-blue-500 text-white';
  if (letter >= 'G' && letter <= 'L') return 'bg-emerald-500 text-white';
  if (letter >= 'M' && letter <= 'R') return 'bg-purple-500 text-white';
  return 'bg-orange-500 text-white';
};
export function EmployeeList() {
  useDocumentTitle('Empleados');
  const {
    user: currentUser,
    impersonateEmployee
  } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('Todas');
  const [selectedBranch, setSelectedBranch] = useState('Todas');
  const [selectedStatus, setSelectedStatus] = useState('Todos');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const companyNames = ['Todas', ...companies.map(c => c.name)];
  const availableBranches = useMemo(() => {
    if (selectedCompany === 'Todas') return ['Todas'];
    const company = companies.find(c => c.name === selectedCompany);
    if (!company || !company.branches) return ['Todas'];
    return ['Todas', ...company.branches.map((b: any) => b.name)];
  }, [selectedCompany, companies]);
  useEffect(() => {
    setSelectedBranch('Todas');
  }, [selectedCompany]);
  // Load companies from Supabase
  const loadCompanies = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getCompanies();
        setCompanies(data);
        // Sync to localStorage for other parts of the app
        localStorage.setItem('criket_companies', JSON.stringify(data));
      } else {
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('criket_companies');
      setCompanies(saved ? JSON.parse(saved) : []);
    }
  };
  // Load employees from Supabase
  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };
  // Load both on mount
  useEffect(() => {
    const loadAll = async () => {
      await loadCompanies();
      await loadEmployees();
    };
    loadAll();
  }, []);
  // Sincronización en tiempo real
  useEffect(() => {
    const subscription = supabaseClient.subscribeToEmployees(() => {
      loadEmployees();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const handleAddEmployee = async (newEmployee: any) => {
    try {
      if (editingEmployee) {
        // Update existing
        await updateEmployee(editingEmployee.id, newEmployee);
        addLog('Empleado Actualizado', `${newEmployee.name} - ${companies.find(c => c.id === newEmployee.companyId)?.name}`, 'employee');
      } else {
        // Add new with secure ID
        const employee = {
          ...newEmployee,
          id: generateSecureId('emp')
        };
        await addEmployee(employee);
        addLog('Empleado Creado', `${newEmployee.name} - ${companies.find(c => c.id === newEmployee.companyId)?.name}`, 'employee');
      }
      // Reload employees after changes
      await loadEmployees();
      setEditingEmployee(null);
      setIsModalOpen(false);
    } catch (error) {
      // Error handled silently - could add toast notification here
    }
  };
  const handleEdit = (employee: any) => {
    setEditingEmployee(employee);
    setIsModalOpen(true);
  };
  const handleDelete = (employee: any) => {
    setDeleteConfirm(employee);
  };
  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteEmployee(deleteConfirm.id);
        addLog('Empleado Eliminado', `${deleteConfirm.name} - ${companies.find(c => c.id === deleteConfirm.companyId)?.name}`, 'employee');
        setDeleteConfirm(null);
        // Reload employees after deletion
        await loadEmployees();
      } catch (error) {
        // Error handled silently
      }
    }
  };
  const handleOpenModal = () => {
    // Check if there are companies before opening modal
    if (companies.length === 0) {
      showToast('warning', 'Sin empresas', 'Debes crear al menos una empresa antes de agregar empleados');
      return;
    }
    setEditingEmployee(null);
    setIsModalOpen(true);
  };
  const handleImpersonate = (employee: any) => {
    impersonateEmployee(employee, companies);
    navigate('/employee/order');
  };
  const filteredEmployees = employees.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) || e.email.toLowerCase().includes(searchTerm.toLowerCase()) || e.username && e.username.toLowerCase().includes(searchTerm.toLowerCase());
    const company = companies.find(c => c.id === e.companyId);
    const matchesCompany = selectedCompany === 'Todas' || company?.name === selectedCompany;
    const branch = company?.branches?.find((b: any) => b.id === e.branchId || b.name === e.branchId);
    const matchesBranch = selectedBranch === 'Todas' || branch?.name === selectedBranch;
    const matchesStatus = selectedStatus === 'Todos' || (selectedStatus === 'Activo' ? e.status === 'Active' : e.status !== 'Active');
    return matchesSearch && matchesCompany && matchesBranch && matchesStatus;
  });
  // Paginación
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany, selectedBranch, selectedStatus]);
  // Count active filters
  const activeFilterCount = [searchTerm, selectedCompany !== 'Todas', selectedBranch !== 'Todas', selectedStatus !== 'Todos'].filter(Boolean).length;
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCompany('Todas');
    setSelectedBranch('Todas');
    setSelectedStatus('Todos');
  };
  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter(e => e.status === 'Active').length,
    inactive: employees.filter(e => e.status !== 'Active').length,
    companies: new Set(employees.map(e => e.companyId)).size
  };
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-3019">
        <div className="flex items-center justify-center py-20" data-id="element-3020">
          <div className="text-center" data-id="element-3021">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" data-id="element-3022"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-3023">
              Cargando empleados...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-id="element-3024">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3025">
        <div data-id="element-3026">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white" data-id="element-3027">
            Empleados
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-3028">
            Gestiona el acceso y perfiles del equipo
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-3030" />} onClick={handleOpenModal} className="shadow-md hover:shadow-lg transition-shadow" data-id="element-3029">
          Nuevo Empleado
        </Button>
      </div>

      {/* Warning if no companies */}
      {companies.length === 0 && <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3" data-id="element-3031">
          <AlertCircle className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" size={20} data-id="element-3032" />
          <div data-id="element-3033">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100" data-id="element-3034">
              No hay empresas registradas
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5" data-id="element-3035">
              Crea una empresa antes de agregar empleados.
            </p>
          </div>
        </div>}

      {/* Stats Cards - 4 columns */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-3036">
        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-blue-500 border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3" data-id="element-3037">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0" data-id="element-3038">
            <Users size={20} className="text-blue-600 dark:text-blue-400" data-id="element-3039" />
          </div>
          <div data-id="element-3040">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-3041">
              Total
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3042">
              {stats.total}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-emerald-500 border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3" data-id="element-3043">
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0" data-id="element-3044">
            <CheckCircle2 size={20} className="text-emerald-600 dark:text-emerald-400" data-id="element-3045" />
          </div>
          <div data-id="element-3046">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-3047">
              Activos
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3048">
              {stats.active}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-red-500 border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3" data-id="element-3049">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0" data-id="element-3050">
            <XCircle size={20} className="text-red-600 dark:text-red-400" data-id="element-3051" />
          </div>
          <div data-id="element-3052">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-3053">
              Inactivos
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3054">
              {stats.inactive}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-purple-500 border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3" data-id="element-3055">
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0" data-id="element-3056">
            <Building size={20} className="text-purple-600 dark:text-purple-400" data-id="element-3057" />
          </div>
          <div data-id="element-3058">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-3059">
              Empresas
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3060">
              {stats.companies}
            </p>
          </div>
        </div>
      </div>

      {/* Filter Bar - Clean horizontal layout */}
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-800" data-id="element-3061">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center" data-id="element-3062">
          <div className="flex-1 min-w-0" data-id="element-3063">
            <SearchBar placeholder="Buscar por nombre, correo o usuario..." className="w-full" onSearch={setSearchTerm} data-id="element-3064" />
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-3065">
            <FilterDropdown value={selectedCompany} options={companyNames} onChange={setSelectedCompany} className="w-full sm:w-40" data-id="element-3066" />
            <FilterDropdown value={selectedBranch} options={availableBranches} onChange={setSelectedBranch} className="w-full sm:w-36" data-id="element-3067" />
            <FilterDropdown value={selectedStatus} options={['Todos', 'Activo', 'Inactivo']} onChange={setSelectedStatus} className="w-full sm:w-32" data-id="element-3068" />
          </div>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap" data-id="element-3069">
              <X size={14} data-id="element-3070" />
              Limpiar ({activeFilterCount})
            </button>}
        </div>
      </div>

      {filteredEmployees.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-xl border border-dashed border-gray-300 dark:border-gray-700" data-id="element-3071">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3072">
            <User size={28} className="text-gray-400" data-id="element-3073" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-3074">
            No se encontraron empleados
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm" data-id="element-3075">
            {activeFilterCount > 0 ? 'Intenta ajustando los filtros' : 'Agrega tu primer empleado para comenzar'}
          </p>
        </div> : <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-3076">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" data-id="element-3077">
            <table className="w-full" data-id="element-3078">
              <thead data-id="element-3079">
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-id="element-3080">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3081">
                    Empleado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3082">
                    Usuario
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3083">
                    Cargo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3084">
                    Ubicación
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3085">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3086">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3087">
                {paginatedEmployees.map((employee, idx) => {
              const company = companies.find(c => c.id === employee.companyId);
              const branch = company?.branches?.find((b: any) => b.id === employee.branchId || b.name === employee.branchId);
              return <tr key={employee.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/30 dark:bg-gray-800/10'}`} data-id="element-3088">
                      <td className="px-4 py-3" data-id="element-3089">
                        <div className="flex items-center gap-3" data-id="element-3090">
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${getAvatarColor(employee.name)}`} data-id="element-3091">
                            {employee.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0" data-id="element-3092">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-3093">
                              {employee.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3094">
                              {employee.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-3095">
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-md font-mono" data-id="element-3096">
                          {employee.username || '—'}
                        </code>
                      </td>
                      <td className="px-4 py-3" data-id="element-3097">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3098">
                          {employee.position || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-3099">
                        <div data-id="element-3100">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3101">
                            {company?.name || '—'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3102">
                            {branch?.name || '—'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-3103">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${employee.status === 'Active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-3104">
                          <span className={`w-1.5 h-1.5 rounded-full ${employee.status === 'Active' ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-3105" />
                          {employee.status === 'Active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-3106">
                        <div className="flex items-center justify-end gap-1" data-id="element-3107">
                          {currentUser?.role === 'admin' && <button onClick={() => handleImpersonate(employee)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Ver como" data-id="element-3108">
                              <Eye size={16} data-id="element-3109" />
                            </button>}
                          <button onClick={() => handleEdit(employee)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3110">
                            <Edit2 size={16} data-id="element-3111" />
                          </button>
                          <button onClick={() => handleDelete(employee)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar" data-id="element-3112">
                            <Trash2 size={16} data-id="element-3113" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3114">
            {paginatedEmployees.map(employee => {
          const company = companies.find(c => c.id === employee.companyId);
          const branch = company?.branches?.find((b: any) => b.id === employee.branchId || b.name === employee.branchId);
          return <div key={employee.id} className="p-4" data-id="element-3115">
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-3" data-id="element-3116">
                    <div className="flex items-center gap-3" data-id="element-3117">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${getAvatarColor(employee.name)}`} data-id="element-3118">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                      <div data-id="element-3119">
                        <p className="font-semibold text-gray-900 dark:text-white" data-id="element-3120">
                          {employee.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3121">
                          {employee.email}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${employee.status === 'Active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-3122">
                      <span className={`w-1.5 h-1.5 rounded-full ${employee.status === 'Active' ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-3123" />
                      {employee.status === 'Active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3" data-id="element-3124">
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2" data-id="element-3125">
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3126">
                        Usuario
                      </p>
                      <p className="font-mono text-gray-900 dark:text-white" data-id="element-3127">
                        {employee.username || '—'}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2" data-id="element-3128">
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3129">Cargo</p>
                      <p className="text-gray-900 dark:text-white" data-id="element-3130">
                        {employee.position || '—'}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2" data-id="element-3131">
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3132">
                        Empresa
                      </p>
                      <p className="text-gray-900 dark:text-white truncate" data-id="element-3133">
                        {company?.name || '—'}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2" data-id="element-3134">
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3135">
                        Sucursal
                      </p>
                      <p className="text-gray-900 dark:text-white truncate" data-id="element-3136">
                        {branch?.name || '—'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-3137">
                    {currentUser?.role === 'admin' && <button onClick={() => handleImpersonate(employee)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors" data-id="element-3138">
                        <Eye size={14} data-id="element-3139" /> Ver como
                      </button>}
                    <button onClick={() => handleEdit(employee)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" data-id="element-3140">
                      <Edit2 size={14} data-id="element-3141" /> Editar
                    </button>
                    <button onClick={() => handleDelete(employee)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors" data-id="element-3142">
                      <Trash2 size={14} data-id="element-3143" /> Eliminar
                    </button>
                  </div>
                </div>;
        })}
          </div>

          {/* Paginación */}
          {totalPages > 1 && <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-3144">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredEmployees.length} itemsPerPage={itemsPerPage} data-id="element-3145" />
            </div>}
        </div>}

      {/* Employee Form Modal */}
      <EmployeeFormModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingEmployee(null);
    }} onSave={handleAddEmployee} editingEmployee={editingEmployee} data-id="element-3146" />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-3147">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-3148">
            <div className="p-6 text-center" data-id="element-3149">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3150">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-3151" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3152">
                ¿Eliminar empleado?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6" data-id="element-3153">
                ¿Estás seguro de que deseas eliminar a{' '}
                <strong data-id="element-3154">{deleteConfirm.name}</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="flex gap-3" data-id="element-3155">
                <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-3156">
                  Cancelar
                </Button>
                <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors" data-id="element-3157">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}